import { CSSProperties, useContext, useEffect, useState } from "react"
import { pageView } from "analytics"
import { UpgradeModalContext } from "Hooks/UpgradeModalContext"
import useOrganizationV2 from 'Hooks/useOrganizationV2'
import useAppView from 'Hooks/useAppView'
import lowerCase from "lodash/lowerCase"
import startCase from "lodash/startCase"
import { useLocation } from "react-router-dom"
import { getStatusToShowPaymentFailedBanner } from "modules/Utils"
import AccountTab from "./Components/AccountTab/AccountTab"
import InvoicesTab from "./Components/InvoicesTab"
import OrganizationSubscriptionStatusBanner from "components/shareable/OrganizationSubscriptionStatusBanner"
import ButtonSymphony from "elements/ButtonSymphony"
import PageSymphony from "elements/PageSymphony"
import useBrand, { BrandExistsState } from "Hooks/useBrand"
import DebugBox from "components/shareable/DebugBox"
import CardSymphony from "elements/CardSymphony"
import TabsSymphony, { TabItem } from "elements/TabsSymphony"
import useScreen from "Hooks/useScreen"
import TabSymphony from "elements/TabSymphony"
import Sty from "modules/Sty"
import PrivacyAndTerms from "components/shareable/PrivacyAndTerms"
import Icon from "elements/Icon"
import Banner from "elements/Banner"
import BillingTabV2 from "./Components/BillingTabV2"

type GlobalWindow = Window & {
    Intercom: (type: string) => void
}

enum PageTabs {
    ACCOUNT = "ACCOUNT",
    BILLING = "BILLING",
    INVOICES = "INVOICES",
    PRIVACY_AND_TERMS = "PRIVACY_AND_TERMS",
}

enum PageTabsViewOnly {
    ACCOUNT = "ACCOUNT",
}

// Create new organization tabs array
const ORGANIZATION_TABS: TabItem[] = [
    PageTabs.ACCOUNT,
].map((tab) => ({
    id: tab,
    title: startCase(lowerCase(tab)),
}))

const SETTINGS_TABS: TabItem[] = [
    PageTabs.ACCOUNT,
    PageTabs.BILLING,
    PageTabs.INVOICES,
    PageTabs.PRIVACY_AND_TERMS,
].map((tab) => ({
    id: tab,
    title: startCase(lowerCase(tab)),
}))

const viewOnlyTabs = [
    PageTabsViewOnly.ACCOUNT,
].map((tab) => ({
    value: tab,
    name: startCase(lowerCase(tab)),
}))

const SettingsPage = () => {
    const {
        mobileView,
    } = useScreen()
    const { currentBrand, isBrandAdmin } = useBrand() as BrandExistsState
    const { handleOpenModal } = useContext(UpgradeModalContext);
    const { isTeamView } = useAppView()
    const { organization } = useOrganizationV2()

    const showPaymentFailedBanner = !isTeamView() ? getStatusToShowPaymentFailedBanner(currentBrand) : false
    let defaultTab: TabItem = SETTINGS_TABS[0];

    // Helper function to check if brand belongs to organization
    const brandIdIsInOrganization = (brandId: number) => {
        return organization?.brands?.some(brand => brand.id === brandId) || false
    }

    // used to check if the brand belongs to the organization
    // so we can render either organization-specific View, or regular pro/free view
    const inOrganization = !isTeamView() ? brandIdIsInOrganization(currentBrand?.id) : true

    if (isTeamView()) {
        // In team view, default to Account tab
        defaultTab = SETTINGS_TABS[0];
    } else if (organization && inOrganization) {
        defaultTab = SETTINGS_TABS[1];
    } else if (isBrandAdmin) {
        defaultTab = SETTINGS_TABS[0];
    }

    const [selectedSettingsTab, setSelectedSettingsTab] = useState<TabItem>(defaultTab);
    const handleClickSupportButton = () => {
        const globalWindow = window as unknown as GlobalWindow
        if (globalWindow.Intercom) {
            globalWindow.Intercom("show")
        }
    }

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const tabFromQuery = queryParams.get("tab");

    useEffect(() => {
        if (tabFromQuery && Object.values(PageTabs).includes(tabFromQuery as PageTabs)) {
            setSelectedSettingsTab(SETTINGS_TABS.find((tab) => tab.id === tabFromQuery) as TabItem);
        } else if (tabFromQuery === 'ADVANCED_SETTINGS' || tabFromQuery === 'CONNECTIONS') {
            // Backward compatibility: redirect old ADVANCED_SETTINGS and CONNECTIONS to account tab
            setSelectedSettingsTab(SETTINGS_TABS.find((tab) => tab.id === PageTabs.ACCOUNT) as TabItem);
        }
    }, [tabFromQuery]);

    useEffect(() => {
        pageView("Settings");
    }, []);

    const renderAccountTab = () => {
        return (
            <TabSymphony className="settingsPageTabAccount" visible={selectedSettingsTab.id === PageTabs.ACCOUNT}>
                <AccountTab />
            </TabSymphony>
        )
    }

    const renderBillingTab = () => {
        return (
            <TabSymphony className="settingsPageTabBilling" visible={selectedSettingsTab.id === PageTabs.BILLING}>
                <BillingTabV2 />
            </TabSymphony>
        )
    }

    const renderInvoicesTab = () => {
        return (
            <TabSymphony className="settingsPageTabInvoices" visible={selectedSettingsTab.id === PageTabs.INVOICES}>
                <InvoicesTab />
            </TabSymphony>
        )
    }

    const renderPrivacyPolicyTab = () => {
        return (
            <TabSymphony className="settingsPageTabPrivacyPolicy" visible={selectedSettingsTab.id === PageTabs.PRIVACY_AND_TERMS}>
                <PrivacyAndTerms />
            </TabSymphony>
        )
    }

    return (
        <PageSymphony className="settingsPageComponent" isContent>
            {showPaymentFailedBanner &&
                <OrganizationSubscriptionStatusBanner />
            }
            <CardSymphony
                className="settingsPageCard"
                title={"Settings"}
                subtitle={isTeamView() ? "Manage your team's account settings, billing, and invoices." : "Manage your account settings and preferences."}
            >
                <TabsSymphony
                    className="settingsPageTabs"
                    selectedTab={selectedSettingsTab}
                    onChange={(tab) => setSelectedSettingsTab(tab as TabItem)}
                    tabs={SETTINGS_TABS}
                    isBorderBottom
                    rightSideContent={!mobileView &&
                        <ButtonSymphony
                            className="settingsPageSupportButton"
                            onClick={handleClickSupportButton}
                        >
                            Support / Help
                        </ButtonSymphony>
                    }
                />
                {renderAccountTab()}
                {renderBillingTab()}
                {renderInvoicesTab()}
                {renderPrivacyPolicyTab()}
            </CardSymphony>

        </PageSymphony>
    )
}

const sty: Record<string, CSSProperties> = {}

export default SettingsPage
